import React, { useEffect, useState } from 'react';
import Modal from 'react-modal';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import ProfileCombo from '../../sharedComponents/ProfileCombo';
import ImageUploadModal from '../../utils/ImageUploadModal';
import useGetCustomerProfile from '../../hooks/useGetCustomerProfile';
import useCaseTicket from './useCaseTicket';
import CaseTicketService from './CaseTicketService';
import OrderPicker from './OrderPicker';
import {
  CUSTOMER_ISSUE_TYPE_OPTIONS, INTERNAL_ISSUE_TYPE_OPTIONS, SHIPPING_CHANNEL_OPTIONS, PRODUCT_GROUPS, toDateOnlyString,
} from './constants';

const customStyles = {
  overlay: { backgroundColor: 'rgba(0,0,0,0.75)', zIndex: 1000 },
  content: {
    top: '5%', left: '50%', right: 'auto', bottom: 'auto',
    width: '720px', maxWidth: '95vw', maxHeight: '90vh',
    transform: 'translateX(-50%)', overflowY: 'auto',
  },
};

// Uses string values in the form ('sea'/'air') to keep MUI Select simple, converted
// to the boolean `isAir` the backend/rest of the app expects on submit.
const SHIP_MODES = [
  { value: 'sea', label: '🚢 เรือ' },
  { value: 'air', label: '✈️ แอร์' },
];

const emptyForm = () => ({
  customerId: null,
  customerName: '',
  orderId: null,
  title: '',
  description: '',
  issueType: 'damaged',
  shippingChannel: '',
  productGroup: '',
  shipMode: '',
  reportedDate: new Date(),
});

// The form asks in the order the admin works: WHAT kind of case, then WHICH
// order(s) — and the customer comes from the order, not a separate search.
//
// `prefill` comes from the quick check: the customer and the parcels it could
// not answer, already as a ตามของ case.
const CreateCaseTicketModal = ({ isOpen, onClose, onCreated, prefill = null }) => {
  // customer = complaint about an order/shipment; internal = system bug /
  // feature request / internal question for Gain or North.
  const [ticketType, setTicketType] = useState('customer');
  const [form, setForm] = useState(emptyForm());
  const [order, setOrder] = useState(null);
  const [hasNoAttachment, setHasNoAttachment] = useState(false);
  const [imageFiles, setImageFiles] = useState([]);   // pasted via ImageUploadModal
  const [videoFiles, setVideoFiles] = useState([]);   // picked via file input
  const [isPasteOpen, setIsPasteOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState([]); // ตามของ only — the parcels being chased

  const { profiles, getCustomerProfiles } = useGetCustomerProfile();
  const { createCaseTicket } = useCaseTicket();

  const isTracking = ticketType === 'customer' && form.issueType === 'tracking_follow';

  useEffect(() => {
    if (!isOpen) return;
    getCustomerProfiles();
    if (prefill) {
      setTicketType('customer');
      setForm({ ...emptyForm(), customerId: prefill.customerId, customerName: prefill.customerName, issueType: 'tracking_follow' });
      setItems(prefill.items || []);
      setHasNoAttachment(true); // a parcel that never arrived has no photo
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const resetAndClose = () => {
    setTicketType('customer');
    setForm(emptyForm());
    setOrder(null);
    setHasNoAttachment(false);
    setImageFiles([]);
    setVideoFiles([]);
    setItems([]);
    onClose?.();
  };

  const handleField = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));

  const handleTypeChange = (type) => {
    setTicketType(type);
    // Each type has its own issueType list — never carry one across.
    setForm((prev) => ({ ...prev, issueType: type === 'internal' ? 'system_bug' : 'damaged' }));
  };

  const handleIssueTypeChange = (value) => {
    setForm((prev) => ({ ...prev, issueType: value, shippingChannel: value === 'damaged' ? prev.shippingChannel : '' }));
    // Switching between a ตามของ case and the others swaps which order field
    // applies, so never carry the other one's pick across.
    if (value === 'tracking_follow') {
      setHasNoAttachment(true);
      setOrder(null);
      handleField('orderId', null);
    } else {
      setItems([]);
    }
  };

  // The order is what identifies the customer — picking one fills the profile in,
  // so the admin never searches the same person twice.
  const applyCustomerFromOrder = (o) => {
    if (form.customerId === o.customerId) return;
    const matched = (profiles || []).find((p) => p.id === o.customerId);
    setForm((prev) => ({
      ...prev,
      customerId: o.customerId,
      customerName: matched?.contact || matched?.smileName || o.smileNameInput || '',
    }));
  };

  // A chased parcel is always a real order — the picker only offers this
  // customer's once one is known, and the backend refuses anything else.
  const addOrderAsItem = (o) => {
    if (items.some((it) => it.orderId === o.id)) { toast.info('ออเดอร์นี้อยู่ในรายการแล้ว'); return; }
    applyCustomerFromOrder(o);
    setItems((prev) => [...prev, {
      orderId: o.id, url: o.url, trackingJp: o.trackingJp, priceYen: o.amount, orderDate: o.orderDate, imageUrl: o.imageUrl,
      isSendOnly: !!o.isSendOnly,
    }]);
  };

  // ฝากส่ง (send-only) parcels skip Kluay and go straight to the supervisor — the
  // backend routes them; a case is all send-only or none (it refuses a mix).
  const sendOnlyCount = items.filter((it) => it.isSendOnly).length;
  const allSendOnly = items.length > 0 && sendOnlyCount === items.length;
  const mixedSendOnly = sendOnlyCount > 0 && sendOnlyCount < items.length;

  const pickSingleOrder = (o) => {
    applyCustomerFromOrder(o);
    setOrder(o);
    handleField('orderId', o.id);
  };

  // ProfileCombo only hands back the smileName string — look up the full record
  // (already fetched in `profiles`) to get the real customerId. Only needed when
  // there is no order to take the customer from. productGroup is deliberately
  // NOT prefilled from the profile — the admin must pick the group of the item
  // in THIS case (a profile default like อื่นๆ would silently pass the required
  // check and pollute the report stats).
  const handleCustomerSelect = (smileName) => {
    const matched = (profiles || []).find((p) => p.smileName === smileName);
    if (!matched) return;
    // Switching customer invalidates anything picked for the old one.
    if (order && order.customerId !== matched.id) {
      setOrder(null);
      handleField('orderId', null);
      toast.info('ล้างออเดอร์ที่เลือกไว้ เพราะไม่ใช่ของลูกค้าคนนี้');
    }
    if (items.length && items[0] && form.customerId !== matched.id) setItems([]);
    setForm((prev) => ({
      ...prev,
      customerId: matched.id,
      customerName: matched.contact || matched.smileName,
    }));
  };

  const handleVideoChange = (e) => {
    const picked = Array.from(e.target.files || []);
    const tooBig = picked.filter((f) => f.size > 200 * 1024 * 1024);
    if (tooBig.length) {
      toast.error(`วิดีโอเกิน 200MB: ${tooBig.map((f) => f.name).join(', ')}`);
    }
    setVideoFiles(picked.filter((f) => f.size <= 200 * 1024 * 1024));
  };

  // Upload proof AFTER the case is created, in the background — a 200MB video can
  // take minutes and must not freeze the modal. Progress lives in a global toast;
  // the detail page refreshes itself when the files land (socket from the backend).
  const uploadInBackground = (ticketId, files) => {
    const toastId = toast.loading(`เคส #${ticketId}: กำลังอัปโหลดไฟล์แนบ 0%`);
    CaseTicketService.uploadAttachments(ticketId, files, (pct) => {
      toast.update(toastId, { render: `เคส #${ticketId}: กำลังอัปโหลดไฟล์แนบ ${pct}%` });
    }).then(() => {
      toast.update(toastId, { render: `เคส #${ticketId}: แนบไฟล์เรียบร้อย`, type: 'success', isLoading: false, autoClose: 4000 });
    }).catch(() => {
      toast.update(toastId, {
        render: `เคส #${ticketId}: อัปโหลดไฟล์แนบไม่สำเร็จ — เปิดเคสแล้วกด "แนบรูป/วิดีโอเพิ่ม" ได้`,
        type: 'error', isLoading: false, autoClose: 10000,
      });
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isInternal = ticketType === 'internal';

    if (isInternal) {
      if (!form.title.trim()) { toast.error('กรุณาระบุหัวข้อ'); return; }
      if (!form.description.trim()) { toast.error('กรุณาระบุรายละเอียด'); return; }
    } else if (isTracking) {
      // ตามของ asks for the parcels and nothing else — the round, sea/air and
      // the date all come from the orders themselves.
      if (items.length === 0) { toast.error('เพิ่มออเดอร์ที่ลูกค้าตามอย่างน้อย 1 รายการ'); return; }
      if (!form.customerId) { toast.error('กรุณาเลือกลูกค้า'); return; }
    } else {
      if (!form.customerId) { toast.error('กรุณาเลือกลูกค้า (เลือกออเดอร์ หรือค้นหาลูกค้า)'); return; }
      if (!form.description.trim()) { toast.error('กรุณาระบุคำอธิบายปัญหา'); return; }
      if (!form.productGroup) { toast.error('กรุณาเลือกกลุ่มสินค้า'); return; }
      if (!form.shipMode) { toast.error('กรุณาเลือกรอบเรือหรือแอร์'); return; }
      if (form.issueType === 'damaged' && !form.shippingChannel) {
        toast.error('กรุณาเลือกช่องทางขนส่ง'); return;
      }
      // Some proof is required — image or video, either is fine — unless the
      // customer explicitly has none.
      if (!hasNoAttachment && imageFiles.length === 0 && videoFiles.length === 0) {
        toast.error('กรุณาเพิ่มรูปหรือวิดีโออย่างน้อย 1 ไฟล์ หรือติ๊ก "ลูกค้าไม่มีรูป/วิดีโอ"'); return;
      }
    }
    const files = [...imageFiles, ...videoFiles];
    if (files.length > 5) {
      toast.error('แนบได้สูงสุด 5 ไฟล์ (รูป + วิดีโอรวมกัน)'); return;
    }

    setSaving(true);
    const payload = isInternal ? {
      type: 'internal',
      title: form.title.trim(),
      description: form.description.trim(),
      issueType: form.issueType,
    } : isTracking ? {
      customerId: form.customerId,
      issueType: 'tracking_follow',
      items,
      hasNoAttachment: imageFiles.length === 0,
    } : {
      customerId: form.customerId,
      orderId: form.orderId,
      description: form.description.trim(),
      issueType: form.issueType,
      shippingChannel: form.issueType === 'damaged' ? form.shippingChannel : null,
      productGroup: form.productGroup,
      isAir: form.shipMode === 'air',
      reportedDate: toDateOnlyString(form.reportedDate),
      hasNoAttachment,
    };
    const created = await createCaseTicket(payload);

    if (!created) { setSaving(false); return; }

    setSaving(false);
    toast.success(created.issueType === 'sendonly_follow'
      ? 'เปิดเคสตามของฝากส่งแล้ว — ส่งให้หัวหน้าตัดสินใจ (ไม่ต้องถาม Kluay)'
      : isTracking ? 'เปิดเคสตามของแล้ว — ส่งให้ Kluay เช็ค' : 'เปิดเคสเรียบร้อย แจ้งหัวหน้าทาง LINE แล้ว');
    onCreated?.(created);
    resetAndClose();

    // After the modal is gone — the admin moves on while big files upload.
    if (files.length > 0) uploadInBackground(created.id, files);
  };

  const pasteImagesButton = (hint) => (
    <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
      <p className="text-sm text-gray-600 mb-2">{hint}</p>
      <button type="button" onClick={() => setIsPasteOpen(true)}
        className="text-sm text-blue-700 border border-blue-300 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-lg">
        📷 เพิ่มรูป{imageFiles.length > 0 ? ` — ${imageFiles.length} รูปแล้ว` : ''}
      </button>
      {imageFiles.length > 0 && (
        <button type="button" onClick={() => setImageFiles([])}
          className="ml-2 text-xs text-gray-400 underline">ล้างรูป</button>
      )}
    </div>
  );

  return (
    <Modal isOpen={isOpen} onRequestClose={resetAndClose} style={customStyles} shouldCloseOnOverlayClick={false}>
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-2xl font-bold">เปิดเคสใหม่</h3>
        <button onClick={resetAndClose} className="text-gray-400 hover:text-gray-600 text-3xl leading-none">×</button>
      </div>

      <form onSubmit={handleSubmit}>
        {/* 1 — Ticket type: customer complaint vs internal (bug / feature request /
            question). Internal skips every customer/shipping field. */}
        <div className="flex gap-2 mb-4">
          <button type="button" onClick={() => handleTypeChange('customer')}
            className={`px-4 py-2 rounded-lg text-sm font-medium border ${
              ticketType === 'customer' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300'
            }`}>
            🧑‍💼 เคสลูกค้า
          </button>
          <button type="button" onClick={() => handleTypeChange('internal')}
            className={`px-4 py-2 rounded-lg text-sm font-medium border ${
              ticketType === 'internal' ? 'bg-rose-600 text-white border-rose-600' : 'bg-white text-gray-600 border-gray-300'
            }`}>
            🛠️ ภายใน (บั๊ก/ขอฟีเจอร์/คำถาม)
          </button>
        </div>

        {ticketType === 'internal' && (
          <>
            <div className="mb-3">
              <TextField
                fullWidth label="หัวข้อ * (แสดงใน LINE และรายการเคส)"
                value={form.title}
                onChange={(e) => handleField('title', e.target.value)}
              />
            </div>
            <div className="mb-3">
              <FormControl fullWidth size="small">
                <InputLabel id="internal-issue-type-label">ประเภท</InputLabel>
                <Select labelId="internal-issue-type-label" label="ประเภท" value={form.issueType}
                  onChange={(e) => handleField('issueType', e.target.value)}>
                  {INTERNAL_ISSUE_TYPE_OPTIONS.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
                </Select>
              </FormControl>
            </div>
            <div className="mb-3">
              <TextField
                fullWidth multiline minRows={3} label="รายละเอียด *"
                value={form.description}
                onChange={(e) => handleField('description', e.target.value)}
              />
            </div>
            {pasteImagesButton('แนบรูป (ไม่บังคับ — เช่น screenshot ของบั๊ก)')}
          </>
        )}

        {ticketType === 'customer' && (<>
        {/* 2 — What kind of case. It decides what the rest of the form asks for,
            so it comes first. */}
        <div className="mb-4">
          <FormControl fullWidth size="small">
            <InputLabel id="issue-type-label">ประเภทปัญหา *</InputLabel>
            <Select labelId="issue-type-label" label="ประเภทปัญหา *" value={form.issueType}
              onChange={(e) => handleIssueTypeChange(e.target.value)}>
              {CUSTOMER_ISSUE_TYPE_OPTIONS.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
            </Select>
          </FormControl>
        </div>

        {/* 3 — The order(s). Picking one names the customer, so nobody searches
            the same person twice. ตามของ takes a list; every other kind takes
            one optional link. */}
        {isTracking ? (
          <div className="mb-4 p-3 bg-orange-50 rounded-lg border border-orange-200">
            <p className="text-sm font-semibold text-orange-900 mb-2">
              📦 ออเดอร์ที่ลูกค้าตาม *
              <span className="block text-xs font-normal text-orange-700">รอบเรือ/แอร์และวันที่ ระบบดึงจากออเดอร์ให้เอง</span>
            </p>

            {items.length > 0 && (
              <ul className="mb-2 flex flex-col gap-1">
                {items.map((it, i) => (
                  <li key={it.orderId} className="flex items-center gap-2 text-sm bg-white border border-orange-100 rounded px-2 py-1">
                    {it.imageUrl && it.imageUrl !== '-' && <img src={it.imageUrl} alt="" className="w-8 h-8 object-cover rounded shrink-0" />}
                    <span className="min-w-0 flex-1">
                      {it.isSendOnly ? (
                        // a send-only order has no link and no price — it is its tracking number
                        <span className="block font-medium text-gray-800">
                          📦 ฝากส่ง · <span className="font-mono">{it.trackingJp && it.trackingJp !== 'ไม่มีเลข' ? it.trackingJp : 'ไม่มีเลขพัสดุ'}</span>
                        </span>
                      ) : (
                        <>
                          <a href={it.url} target="_blank" rel="noreferrer" className="block truncate text-blue-700 underline">
                            {it.url}
                          </a>
                          <span className="block text-xs text-gray-500">
                            {it.priceYen != null ? `${Number(it.priceYen).toLocaleString()} เยน` : '—'}
                            {' · '}
                            {it.trackingJp && it.trackingJp !== 'ไม่มีเลข' ? it.trackingJp : 'ไม่มีเลขพัสดุ'}
                          </span>
                        </>
                      )}
                    </span>
                    <button type="button" onClick={() => setItems((prev) => prev.filter((_, j) => j !== i))}
                      className="text-gray-400 hover:text-red-600 shrink-0">✕</button>
                  </li>
                ))}
              </ul>
            )}
            {mixedSendOnly && (
              <p className="text-sm text-red-700 font-medium mb-2">
                ⚠️ แยกเคสฝากส่ง — รายการฝากส่ง ({sendOnlyCount}) ต้องเปิดเป็นเคสแยกจากรายการสั่งซื้อ
              </p>
            )}
            {allSendOnly && (
              <p className="text-xs text-amber-800 mb-2">ฝากส่ง: ไม่ต้องถาม Kluay — เคสจะไปที่หัวหน้าตัดสินใจเลย</p>
            )}

            <OrderPicker
              customerId={form.customerId}
              pickedIds={items.map((it) => it.orderId)}
              onPick={addOrderAsItem}
              label="ค้นหาออเดอร์ที่ลูกค้าตาม (ชื่อ / ลิงก์ / เลขพัสดุ)"
            />
          </div>
        ) : (
          <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-sm font-semibold text-gray-600 mb-2">ค้นหาออเดอร์ (ไม่บังคับ — เชื่อมเคสกับออเดอร์นี้)</p>
            <OrderPicker customerId={form.customerId} onPick={pickSingleOrder} />
            {order && (
              <p className="text-sm text-green-700 mt-2 truncate">✓ เลือกออเดอร์: {order.url} — {order.amount} บาท</p>
            )}
          </div>
        )}

        {/* 4 — The customer. Filled in by the order above; the search is the
            fallback for a case with no order to point at. */}
        <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
          {form.customerId ? (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-green-700 font-medium">✓ ลูกค้า: {form.customerName}</span>
              <button type="button"
                onClick={() => { setForm((prev) => ({ ...prev, customerId: null, customerName: '' })); setOrder(null); setItems([]); }}
                className="text-xs text-gray-500 underline">เปลี่ยนลูกค้า</button>
            </div>
          ) : (
            <>
              <p className="text-sm font-semibold text-blue-800 mb-2">ลูกค้า * — เลือกออเดอร์ด้านบน หรือค้นหาที่นี่</p>
              <ProfileCombo profiles={profiles || []} onChange={handleCustomerSelect} initialSmileName={form.customerName} />
            </>
          )}
        </div>

        {/* 5 — Everything below describes a complaint. ตามของ has nothing to
            describe: the parcels ARE the case. */}
        {isTracking ? pasteImagesButton('แนบรูป (ไม่บังคับ — เช่น screenshot แชทที่ลูกค้าทวง)') : (
          <>
            <div className="mb-3">
              <TextField
                fullWidth multiline minRows={3} label="คำอธิบายปัญหา *"
                placeholder="คัดลอกข้อความของลูกค้ามาวางได้เลย — แต่ถ้าลูกค้าเล่าไม่ชัดเจน ให้ถามรายละเอียดเพิ่มให้ครบก่อน แล้วค่อยวาง/เปิดเคส"
                InputLabelProps={{ shrink: true }}
                value={form.description}
                onChange={(e) => handleField('description', e.target.value)}
              />
            </div>

            <div className="grid grid-cols-2 gap-3 mb-3">
              <FormControl fullWidth size="small">
                <InputLabel id="product-group-label">กลุ่มสินค้า *</InputLabel>
                <Select labelId="product-group-label" label="กลุ่มสินค้า *" value={form.productGroup}
                  onChange={(e) => handleField('productGroup', e.target.value)}>
                  {PRODUCT_GROUPS.map((g) => <MenuItem key={g} value={g}>{g}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel id="ship-mode-label">รอบเรือ/แอร์ *</InputLabel>
                <Select labelId="ship-mode-label" label="รอบเรือ/แอร์ *" value={form.shipMode}
                  onChange={(e) => handleField('shipMode', e.target.value)}>
                  {SHIP_MODES.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                </Select>
              </FormControl>
            </div>

            {form.issueType === 'damaged' && (
              <div className="mb-3">
                <FormControl fullWidth size="small">
                  <InputLabel id="shipping-channel-label">ช่องทางขนส่ง *</InputLabel>
                  <Select labelId="shipping-channel-label" label="ช่องทางขนส่ง *" value={form.shippingChannel}
                    onChange={(e) => handleField('shippingChannel', e.target.value)}>
                    {SHIPPING_CHANNEL_OPTIONS.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </div>
            )}

            <div className="mb-4">
              <InputLabel style={{ fontSize: '0.9em', color: 'black', marginBottom: 4 }}>วันที่แจ้งเคสมา</InputLabel>
              <DatePicker
                selected={form.reportedDate}
                onChange={(date) => handleField('reportedDate', date)}
                dateFormat="yyyy-MM-dd"
                className="datepicker2 w-full"
              />
            </div>

            <div className="mb-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
              <p className="text-sm font-semibold text-yellow-800 mb-1">📎 หลักฐานจากลูกค้า</p>
              <p className="text-xs text-yellow-700 mb-2">ต้องมีรูปหรือวิดีโออย่างน้อย 1 ไฟล์ (อย่างใดอย่างหนึ่งก็ได้)</p>

              {!hasNoAttachment && (
                <>
                  <div className="mb-3">
                    <p className="text-sm text-gray-700 mb-1">รูปภาพ</p>
                    <button type="button" onClick={() => setIsPasteOpen(true)}
                      className="text-sm text-blue-700 border border-blue-300 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-lg">
                      📷 เพิ่มรูป{imageFiles.length > 0 ? ` — ${imageFiles.length} รูปแล้ว` : ''}
                    </button>
                    {imageFiles.length > 0 && (
                      <button type="button" onClick={() => setImageFiles([])}
                        className="ml-2 text-xs text-gray-400 underline">ล้างรูป</button>
                    )}
                  </div>
                  <div className="mb-1">
                    <p className="text-sm text-gray-700 mb-1">วิดีโอ <span className="text-gray-400">(ไม่เกิน 200MB/ไฟล์ · อัปโหลดต่อเบื้องหลังหลังเปิดเคส)</span></p>
                    <input type="file" multiple accept="video/*" onChange={handleVideoChange} />
                    {videoFiles.length > 0 && (
                      <p className="text-xs text-gray-500 mt-1">
                        เลือกวิดีโอแล้ว {videoFiles.length} ไฟล์ ({videoFiles.map((f) => f.name).join(', ')})
                      </p>
                    )}
                  </div>
                </>
              )}

              <label className="flex items-center gap-2 mt-2 text-sm border-t border-yellow-200 pt-2">
                <input type="checkbox" checked={hasNoAttachment}
                  onChange={(e) => { setHasNoAttachment(e.target.checked); if (e.target.checked) { setImageFiles([]); setVideoFiles([]); } }} />
                ลูกค้าไม่มีรูป/วิดีโอ (เปิดเคสโดยไม่มีหลักฐาน)
              </label>
            </div>
          </>
        )}
        </>)}

        <div className="flex justify-end gap-3">
          <button type="button" onClick={resetAndClose}
            className="px-5 py-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50">ยกเลิก</button>
          <button type="submit" disabled={saving || (isTracking && (mixedSendOnly || items.length === 0))}
            className={`px-5 py-2 disabled:opacity-50 text-white rounded-lg font-medium ${
              isTracking ? 'bg-orange-600 hover:bg-orange-700' : 'bg-green-600 hover:bg-green-700'
            }`}>
            {saving ? 'กำลังบันทึก...'
              : !isTracking ? 'เปิดเคส'
                : items.length === 0 ? '👆 เลือกออเดอร์ที่ลูกค้าตามก่อน'
                  : allSendOnly ? '📦 ส่งให้หัวหน้า — เปิดเคสตามของฝากส่ง'
                  : '📦 ส่งให้ Kluay เช็ค — เปิดเคสตามของ'}
          </button>
        </div>
      </form>

      {/* Paste-based image attach — same UX as receipt/slip uploads elsewhere.
          Confirmed files accumulate; opening again adds more. */}
      <ImageUploadModal
        isOpen={isPasteOpen}
        onRequestClose={() => setIsPasteOpen(false)}
        onConfirm={(pasted) => setImageFiles((prev) => [...prev, ...pasted])}
        title="วางรูปหลักฐาน (Ctrl+V)"
        maxFiles={5}
        maxFileSize={15 * 1024 * 1024}
      />
    </Modal>
  );
};

export default CreateCaseTicketModal;
